let Shaders = {};

Shaders["pointcloud.vs"] = `
precision highp float;
precision highp int;

#define max_clip_polygons 8
#define PI 3.141592653589793

in vec3 position;
in vec3 color;
in float intensity;
in float classification;
in float returnNumber;
in float numberOfReturns;
in float pointSourceID;
in vec4 indices;
in float spacing;
in float gpsTime;
in float isSelected;
in float temperature;
in vec3 normal;
in float aExtra;

uniform mat4 modelMatrix;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform mat4 viewMatrix;
uniform mat4 uViewInv;

uniform float uScreenWidth;
uniform float uScreenHeight;
uniform float fov;
uniform float near;
uniform float far;

uniform bool uDebug;

uniform bool uUseOrthographicCamera;
uniform float uOrthoWidth;
uniform float uOrthoHeight;

#define CLIPTASK_NONE 0
#define CLIPTASK_HIGHLIGHT 1
#define CLIPTASK_SHOW_INSIDE 2
#define CLIPTASK_SHOW_OUTSIDE 3

#define CLIPMETHOD_INSIDE_ANY 0
#define CLIPMETHOD_INSIDE_ALL 1

uniform int clipTask;
uniform int clipMethod;
#if defined(num_clipboxes) && num_clipboxes > 0
	uniform mat4 clipBoxes[num_clipboxes];
	uniform int clipHides[num_clipboxes];
	//uniform vec3 clipBoxMeshes[num_clipboxes];
#endif

// 设备区域着色(与裁剪 clip 完全独立)：盒内的点按盒色混合，intensity 控制强度/闪烁。
#if defined(num_colorboxes) && num_colorboxes > 0
	uniform mat4 colorBoxes[num_colorboxes];
	uniform vec3 colorBoxColors[num_colorboxes];
	uniform float colorBoxIntensity[num_colorboxes];
#endif

#if defined(num_clipspheres) && num_clipspheres > 0
	uniform mat4 uClipSpheres[num_clipspheres];
#endif

#if defined(num_clippolygons) && num_clippolygons > 0
	uniform int uClipPolygonVCount[num_clippolygons];
	uniform vec3 uClipPolygonVertices[num_clippolygons * 8];
	uniform mat4 uClipPolygonWVP[num_clippolygons];
#endif

// 多边形棱柱裁剪（YDPolygonVolume）：顶点保持局部坐标上传（原点=质心），另传各
// volume 的 matrixWorld 逆矩阵把世界点变回局部再判定——volume 被整体平移/旋转后
// 语义仍正确（与 BoxVolume 的 clipBoxes 逆矩阵模式一致）。判定 = 局部 XY even-odd
// （天然支持凹/自交多边形）+ 局部 Z 闭区间。
#if defined(num_clipprisms) && num_clipprisms > 0
	#define max_clip_prism_vertices 32
	uniform mat4 uClipPrismInvMatrix[num_clipprisms];
	uniform vec3 uClipPrismVertices[num_clipprisms * 32];
	uniform int uClipPrismVCount[num_clipprisms];
	uniform vec2 uClipPrismZRange[num_clipprisms];
#endif


uniform float size;
uniform float minSize;
uniform float maxSize;

uniform float uPCIndex;
uniform float uOctreeSpacing;
uniform float uNodeSpacing;
uniform float uOctreeSize;
uniform vec3 uBBSize;
uniform float uLevel;
uniform float uVNStart;
uniform bool uIsLeafNode;

uniform vec3 uColor;
uniform float uOpacity;

uniform vec2 elevationRange;
uniform vec2 intensityRange;

uniform vec2 uFilterReturnNumberRange;
uniform vec2 uFilterNumberOfReturnsRange;
uniform vec2 uFilterPointSourceIDClipRange;
uniform vec2 uFilterGPSTimeClipRange;
uniform float uGpsScale;
uniform float uGpsOffset;

uniform vec2 uNormalizedGpsBufferRange;

uniform vec3 uIntensity_gbc;
uniform vec3 uRGB_gbc;
uniform vec3 uExtra_gbc;

uniform float uTransition;
uniform float wRGB;
uniform float wIntensity;
uniform float wElevation;
uniform float wClassification;
uniform float wReturnNumber;
uniform float wSourceID;

uniform vec2 uExtraNormalizedRange;
uniform vec2 uExtraRange;
uniform float uExtraScale;
uniform float uExtraOffset;

uniform vec3 uShadowColor;

uniform sampler2D visibleNodes;
uniform sampler2D gradient;
uniform sampler2D classificationLUT;

#if defined(color_type_matcap)
uniform sampler2D matcapTextureUniform;
#endif
uniform bool backfaceCulling;

uniform float minTemperature;
uniform float maxTemperature;
uniform bool showTemperature;

#if defined(num_shadowmaps) && num_shadowmaps > 0
uniform sampler2D uShadowMap[num_shadowmaps];
uniform mat4 uShadowWorldView[num_shadowmaps];
uniform mat4 uShadowProj[num_shadowmaps];
#endif

out vec4	vColor;
out vec4	vTemperatureColor;
out float	vLogDepth;
out vec3	vViewPosition;
out float 	vRadius;
out float 	vPointSize;

// 温度颜色映射表（256个RGB值）
const float colorR[256] = float[256]( 0.050383, 0.063536, 0.075353, 0.086222, 0.096379, 0.105980, 0.115124, 0.123903, 0.132381, 0.140603, 0.148607, 0.156421, 0.164070, 0.171574, 0.178950, 0.186213, 0.193374, 0.200445, 0.207435, 0.214350, 0.221197, 0.227983, 0.234715, 0.241396, 0.248032, 0.254627, 0.261183, 0.267703, 0.274191, 0.280648, 0.287076, 0.293478, 0.299855, 0.306210, 0.312543, 0.318856, 0.325150, 0.331426, 0.337683, 0.343925, 0.350150, 0.356359, 0.362553, 0.368733, 0.374897, 0.381047, 0.387183, 0.393304, 0.399411, 0.405503, 0.411580, 0.417642, 0.423689, 0.429719, 0.435734, 0.441732, 0.447714, 0.453677, 0.459623, 0.465550, 0.471457, 0.477344, 0.483210, 0.489055, 0.494877, 0.500678, 0.506454, 0.512206, 0.517933, 0.523633, 0.529306, 0.534952, 0.540570, 0.546157, 0.551715, 0.557243, 0.562738, 0.568201, 0.573632, 0.579029, 0.584391, 0.589719, 0.595011, 0.600266, 0.605485, 0.610667, 0.615812, 0.620919, 0.625987, 0.631017, 0.636008, 0.640959, 0.645872, 0.650746, 0.655580, 0.660374, 0.665129, 0.669845, 0.674522, 0.679160, 0.683758, 0.688318, 0.692840, 0.697324, 0.701769, 0.706178, 0.710549, 0.714883, 0.719181, 0.723444, 0.727670, 0.731862, 0.736019, 0.740143, 0.744232, 0.748289, 0.752312, 0.756304, 0.760264, 0.764193, 0.768090, 0.771958, 0.775796, 0.779604, 0.783383, 0.787133, 0.790855, 0.794549, 0.798216, 0.801855, 0.805467, 0.809052, 0.812612, 0.816144, 0.819651, 0.823132, 0.826588, 0.830018, 0.833422, 0.836801, 0.840155, 0.843484, 0.846788, 0.850066, 0.853319, 0.856547, 0.859750, 0.862927, 0.866078, 0.869203, 0.872303, 0.875376, 0.878423, 0.881443, 0.884436, 0.887402, 0.890340, 0.893250, 0.896131, 0.898984, 0.901807, 0.904601, 0.907365, 0.910098, 0.912800, 0.915471, 0.918109, 0.920714, 0.923287, 0.925825, 0.928329, 0.930798, 0.933232, 0.935630, 0.937990, 0.940313, 0.942598, 0.944844, 0.947051, 0.949217, 0.951344, 0.953428, 0.955470, 0.957469, 0.959424, 0.961336, 0.963203, 0.965024, 0.966798, 0.968526, 0.970205, 0.971835, 0.973416, 0.974947, 0.976428, 0.977856, 0.979233, 0.980556, 0.981826, 0.983041, 0.984199, 0.985301, 0.986345, 0.987332, 0.988260, 0.989128, 0.989935, 0.990681, 0.991365, 0.991985, 0.992541, 0.993032, 0.993456, 0.993814, 0.994103, 0.994324, 0.994474, 0.994553, 0.994561, 0.994495, 0.994355, 0.994141, 0.993851, 0.993482, 0.993033, 0.992505, 0.991897, 0.991209, 0.990439, 0.989587, 0.988648, 0.987621, 0.986509, 0.985314, 0.984031, 0.982653, 0.981190, 0.979644, 0.977995, 0.976265, 0.974443, 0.972530, 0.970533, 0.968443, 0.966271, 0.964021, 0.961681, 0.959276, 0.956808, 0.954287, 0.951726, 0.949151, 0.946602, 0.944152, 0.941896, 0.940015f );
const float colorG[256] = float[256]( 0.029803, 0.028426, 0.027206, 0.026125, 0.025165, 0.024309, 0.023556, 0.022878, 0.022258, 0.021687, 0.021154, 0.020651, 0.020171, 0.019706, 0.019252, 0.018803, 0.018354, 0.017902, 0.017442, 0.016973, 0.016497, 0.016007, 0.015502, 0.014979, 0.014439, 0.013882, 0.013308, 0.012716, 0.012109, 0.011488, 0.010855, 0.010213, 0.009561, 0.008902, 0.008239, 0.007576, 0.006915, 0.006261, 0.005618, 0.004991, 0.004382, 0.003798, 0.003243, 0.002724, 0.002245, 0.001814, 0.001434, 0.001114, 0.000859, 0.000678, 0.000577, 0.000564, 0.000646, 0.000831, 0.001127, 0.001540, 0.002080, 0.002755, 0.003574, 0.004545, 0.005678, 0.006980, 0.008460, 0.010127, 0.011990, 0.014055, 0.016333, 0.018833, 0.021563, 0.024532, 0.027747, 0.031217, 0.034950, 0.038954, 0.043136, 0.047331, 0.051545, 0.055778, 0.060028, 0.064296, 0.068579, 0.072878, 0.077190, 0.081516, 0.085854, 0.090204, 0.094564, 0.098934, 0.103312, 0.107699, 0.112092, 0.116492, 0.120898, 0.125309, 0.129725, 0.134144, 0.138566, 0.142992, 0.147419, 0.151848, 0.156278, 0.160709, 0.165141, 0.169573, 0.174005, 0.178437, 0.182868, 0.187299, 0.191729, 0.196158, 0.200586, 0.205013, 0.209439, 0.213864, 0.218288, 0.222711, 0.227133, 0.231555, 0.235976, 0.240396, 0.244817, 0.249237, 0.253658, 0.258078, 0.262500, 0.266922, 0.271345, 0.275770, 0.280197, 0.284626, 0.289057, 0.293491, 0.297928, 0.302368, 0.306812, 0.311261, 0.315714, 0.320172, 0.324635, 0.329105, 0.333580, 0.338062, 0.342551, 0.347048, 0.351553, 0.356066, 0.360588, 0.365119, 0.369660, 0.374212, 0.378774, 0.383347, 0.387932, 0.392529, 0.397139, 0.401762, 0.406398, 0.411048, 0.415712, 0.420392, 0.425087, 0.429797, 0.434524, 0.439268, 0.444029, 0.448807, 0.453603, 0.458417, 0.463251, 0.468103, 0.472975, 0.477867, 0.482780, 0.487712, 0.492667, 0.497642, 0.502639, 0.507658, 0.512699, 0.517763, 0.522850, 0.527960, 0.533093, 0.538250, 0.543431, 0.548636, 0.553865, 0.559118, 0.564396, 0.569700, 0.575028, 0.580382, 0.585761, 0.591165, 0.596595, 0.602051, 0.607532, 0.613039, 0.618572, 0.624131, 0.629718, 0.635330, 0.640969, 0.646633, 0.652325, 0.658043, 0.663787, 0.669558, 0.675355, 0.681179, 0.687030, 0.692907, 0.698810, 0.704741, 0.710698, 0.716681, 0.722691, 0.728728, 0.734791, 0.740880, 0.746995, 0.753137, 0.759304, 0.765499, 0.771720, 0.777967, 0.784239, 0.790537, 0.796859, 0.803205, 0.809579, 0.815978, 0.822401, 0.828846, 0.835315, 0.841812, 0.848329, 0.854866, 0.861432, 0.868016, 0.874622, 0.881250, 0.887896, 0.894564, 0.901249, 0.907950, 0.914672, 0.921407, 0.928152, 0.934908, 0.941671, 0.948435, 0.955190, 0.961916, 0.968590, 0.975158f );
const float colorB[256] = float[256]( 0.527975, 0.533124, 0.538007, 0.542658, 0.547103, 0.551368, 0.555468, 0.559423, 0.563250, 0.566959, 0.570562, 0.574065, 0.577478, 0.580806, 0.584054, 0.587228, 0.590330, 0.593364, 0.596333, 0.599239, 0.602083, 0.604867, 0.607592, 0.610259, 0.612868, 0.615419, 0.617911, 0.620346, 0.622722, 0.625038, 0.627295, 0.629490, 0.631624, 0.633694, 0.635700, 0.637640, 0.639512, 0.641316, 0.643049, 0.644710, 0.646298, 0.647810, 0.649245, 0.650601, 0.651876, 0.653068, 0.654177, 0.655199, 0.656133, 0.656977, 0.657730, 0.658390, 0.658956, 0.659425, 0.659797, 0.660069, 0.660240, 0.660310, 0.660277, 0.660139, 0.659897, 0.659549, 0.659095, 0.658534, 0.657865, 0.657088, 0.656202, 0.655209, 0.654109, 0.652901, 0.651586, 0.650165, 0.648640, 0.647010, 0.645277, 0.643443, 0.641509, 0.639477, 0.637349, 0.635126, 0.632812, 0.630408, 0.627917, 0.625342, 0.622686, 0.619951, 0.617140, 0.614257, 0.611305, 0.608287, 0.605205, 0.602065, 0.598867, 0.595617, 0.592317, 0.588971, 0.585582, 0.582154, 0.578688, 0.575189, 0.571660, 0.568103, 0.564522, 0.560919, 0.557296, 0.553657, 0.550004, 0.546338, 0.542663, 0.538981, 0.535293, 0.531601, 0.527908, 0.524216, 0.520524, 0.516834, 0.513149, 0.509468, 0.505794, 0.502126, 0.498465, 0.494813, 0.491171, 0.487539, 0.483918, 0.480307, 0.476706, 0.473117, 0.469538, 0.465971, 0.462415, 0.458870, 0.455338, 0.451816, 0.448306, 0.444806, 0.441316, 0.437836, 0.434366, 0.430905, 0.427455, 0.424013, 0.420579, 0.417153, 0.413734, 0.410322, 0.406917, 0.403519, 0.400126, 0.396738, 0.393355, 0.389976, 0.386600, 0.383229, 0.379860, 0.376494, 0.373130, 0.369768, 0.366407, 0.363047, 0.359688, 0.356329, 0.352970, 0.349610, 0.346251, 0.342890, 0.339529, 0.336166, 0.332801, 0.329435, 0.326067, 0.322697, 0.319325, 0.315952, 0.312575, 0.309197, 0.305816, 0.302433, 0.299049, 0.295662, 0.292275, 0.288883, 0.285490, 0.282096, 0.278701, 0.275305, 0.271909, 0.268513, 0.265118, 0.261721, 0.258325, 0.254931, 0.251540, 0.248151, 0.244767, 0.241387, 0.238013, 0.234646, 0.231287, 0.227937, 0.224595, 0.221265, 0.217948, 0.214648, 0.211364, 0.208100, 0.204859, 0.201642, 0.198453, 0.195295, 0.192170, 0.189084, 0.186041, 0.183043, 0.180097, 0.177208, 0.174381, 0.171622, 0.168938, 0.166335, 0.163821, 0.161404, 0.159092, 0.156891, 0.154808, 0.152855, 0.151042, 0.149377, 0.147870, 0.146529, 0.145357, 0.144363, 0.143557, 0.142945, 0.142528, 0.142303, 0.142279, 0.142453, 0.142808, 0.143351, 0.144061, 0.144923, 0.145919, 0.147014, 0.148180, 0.149370, 0.150520, 0.151566, 0.152409, 0.152921, 0.152925, 0.152178, 0.150328, 0.146861, 0.140956, 0.131326f );

// 温度颜色映射函数
vec4 colorMap(float temp) {

    float rdata = (temp - minTemperature) / (maxTemperature - minTemperature);

    // 确保温度在有效范围内
    if(temp < minTemperature) {
        temp = minTemperature;
    }
    if(temp > maxTemperature) {
        temp = maxTemperature;
    }

    // 计算数组索引
    int p = int((temp - minTemperature) * 255.0 / (maxTemperature - minTemperature));

    // 确保索引在有效范围内
    if(p < 0) p = 0;
    if(p > 255) p = 255;

    float red = colorR[p];
    float green = colorG[p];
    float blue = colorB[p];

    return vec4(
        red,
        green,
        blue,
        1.0);
}

float customRound(float number){
	return floor(number + 0.5);
}

// 
//    ###    ########     ###    ########  ######## #### ##     ## ########     ######  #### ######## ########  ######  
//   ## ##   ##     ##   ## ##   ##     ##    ##     ##  ##     ## ##          ##    ##  ##       ##  ##       ##    ## 
//  ##   ##  ##     ##  ##   ##  ##     ##    ##     ##  ##     ## ##          ##        ##      ##   ##       ##       
// ##     ## ##     ## ##     ## ########     ##     ##  ##     ## ######       ######   ##     ##    ######    ######  
// ######### ##     ## ######### ##           ##     ##   ##   ##  ##                ##  ##    ##     ##             ## 
// ##     ## ##     ## ##     ## ##           ##     ##    ## ##   ##          ##    ##  ##   ##      ##       ##    ## 
// ##     ## ########  ##     ## ##           ##    ####    ###    ########     ######  #### ######## ########  ######  
// 																			


// ---------------------
// OCTREE
// ---------------------

#if (defined(adaptive_point_size) || defined(color_type_level_of_detail)) && defined(tree_type_octree)
/**
 * number of 1-bits up to inclusive index position
 * number is treated as if it were an integer in the range 0-255
 *
 */
int numberOfOnes(int number, int index){
	int numOnes = 0;
	int tmp = 128;
	for(int i = 7; i >= 0; i--){
		
		if(number >= tmp){
			number = number - tmp;

			if(i <= index){
				numOnes++;
			}
		}
		
		tmp = tmp / 2;
	}

	return numOnes;
}


/**
 * checks whether the bit at index is 1
 * number is treated as if it were an integer in the range 0-255
 *
 */
bool isBitSet(int number, int index){

	// weird multi else if due to lack of proper array, int and bitwise support in WebGL 1.0
	int powi = 1;
	if(index == 0){
		powi = 1;
	}else if(index == 1){
		powi = 2;
	}else if(index == 2){
		powi = 4;
	}else if(index == 3){
		powi = 8;
	}else if(index == 4){
		powi = 16;
	}else if(index == 5){
		powi = 32;
	}else if(index == 6){
		powi = 64;
	}else if(index == 7){
		powi = 128;
	}else{
		return false;
	}

	int ndp = number / powi;

	return mod(float(ndp), 2.0) != 0.0;
}


/**
 * find the LOD at the point position
 */
float getLOD(){
	
	vec3 offset = vec3(0.0, 0.0, 0.0);
	int iOffset = int(uVNStart);
	float depth = uLevel;
	for(float i = 0.0; i <= 30.0; i++){
		float nodeSizeAtLevel = uOctreeSize / pow(2.0, i + uLevel + 0.0);
		
		vec3 index3d = (position-offset) / nodeSizeAtLevel;
		index3d = floor(index3d + 0.5);
		int index = int(customRound(4.0 * index3d.x + 2.0 * index3d.y + index3d.z));
		
		vec4 value = texture(visibleNodes, vec2(float(iOffset) / 2048.0, 0.0));
		int mask = int(customRound(value.r * 255.0));

		if(isBitSet(mask, index)){
			// there are more visible child nodes at this position
			int advanceG = int(customRound(value.g * 255.0)) * 256;
			int advanceB = int(customRound(value.b * 255.0));
			int advanceChild = numberOfOnes(mask, index - 1);
			int advance = advanceG + advanceB + advanceChild;

			iOffset = iOffset + advance;
			
			depth++;
		}else{
			// no more visible child nodes at this position
			//return value.a * 255.0;

			float lodOffset = (255.0 * value.a) / 10.0 - 10.0;

			return depth  + lodOffset;
		}
		
		offset = offset + (vec3(1.0, 1.0, 1.0) * nodeSizeAtLevel * 0.5) * index3d;
	}
		
	return depth;
}

float getSpacing(){
	vec3 offset = vec3(0.0, 0.0, 0.0);
	int iOffset = int(uVNStart);
	float depth = uLevel;
	float spacing = uNodeSpacing;
	for(float i = 0.0; i <= 30.0; i++){
		float nodeSizeAtLevel = uOctreeSize / pow(2.0, i + uLevel + 0.0);
		
		vec3 index3d = (position-offset) / nodeSizeAtLevel;
		index3d = floor(index3d + 0.5);
		int index = int(customRound(4.0 * index3d.x + 2.0 * index3d.y + index3d.z));
		
		vec4 value = texture(visibleNodes, vec2(float(iOffset) / 2048.0, 0.0));
		int mask = int(customRound(value.r * 255.0));
		float spacingFactor = value.a;

		if(i > 0.0){
			spacing = spacing / (255.0 * spacingFactor);
		}
		

		if(isBitSet(mask, index)){
			// there are more visible child nodes at this position
			int advanceG = int(customRound(value.g * 255.0)) * 256;
			int advanceB = int(customRound(value.b * 255.0));
			int advanceChild = numberOfOnes(mask, index - 1);
			int advance = advanceG + advanceB + advanceChild;

			iOffset = iOffset + advance;

			//spacing = spacing / (255.0 * spacingFactor);
			//spacing = spacing / 3.0;
			
			depth++;
		}else{
			// no more visible child nodes at this position
			return spacing;
		}
		
		offset = offset + (vec3(1.0, 1.0, 1.0) * nodeSizeAtLevel * 0.5) * index3d;
	}
		
	return spacing;
}

float getPointSizeAttenuation(){
	return pow(2.0, getLOD());
}


#endif


// ---------------------
// KD-TREE
// ---------------------

#if (defined(adaptive_point_size) || defined(color_type_level_of_detail)) && defined(tree_type_kdtree)

float getLOD(){
	vec3 offset = vec3(0.0, 0.0, 0.0);
	float iOffset = 0.0;
	float depth = 0.0;
		
		
	vec3 size = uBBSize;	
	vec3 pos = position;
		
	for(float i = 0.0; i <= 1000.0; i++){
		
		vec4 value = texture(visibleNodes, vec2(iOffset / 2048.0, 0.0));
		
		int children = int(value.r * 255.0);
		float next = value.g * 255.0;
		int split = int(value.b * 255.0);
		
		if(next == 0.0){
		 	return depth;
		}
		
		vec3 splitv = vec3(0.0, 0.0, 0.0);
		if(split == 1){
			splitv.x = 1.0;
		}else if(split == 2){
		 	splitv.y = 1.0;
		}else if(split == 4){
		 	splitv.z = 1.0;
		}
		
		iOffset = iOffset + next;
		
		float factor = length(pos * splitv / size);
		if(factor < 0.5){
			// left
		if(children == 0 || children == 2){
				return depth;
			}
		}else{
			// right
			pos = pos - size * splitv * 0.5;
			if(children == 0 || children == 1){
				return depth;
			}
			if(children == 3){
				iOffset = iOffset + 1.0;
			}
		}
		size = size * ((1.0 - (splitv + 1.0) / 2.0) + 0.5);
		
		depth++;
	}
		
		
	return depth;	
}

float getPointSizeAttenuation(){
	return 0.5 * pow(1.3, getLOD());
}

#endif



// 
//    ###    ######## ######## ########  #### ########  ##     ## ######## ########  ######  
//   ## ##      ##       ##    ##     ##  ##  ##     ## ##     ##    ##    ##       ##    ## 
//  ##   ##     ##       ##    ##     ##  ##  ##     ## ##     ##    ##    ##       ##       
// ##     ##    ##       ##    ########   ##  ########  ##     ##    ##    ######    ######  
// #########    ##       ##    ##   ##    ##  ##     ## ##     ##    ##    ##             ## 
// ##     ##    ##       ##    ##    ##   ##  ##     ## ##     ##    ##    ##       ##    ## 
// ##     ##    ##       ##    ##     ## #### ########   #######     ##    ########  ######                                                                               
// 

// 新增：阴影可见度计算封装函数
#if defined(num_shadowmaps) && num_shadowmaps > 0
float calculateShadowVisibility(
	mat4 shadowWorldView,
	mat4 shadowProj,
	sampler2D shadowMap,
	vec3 pos,
	float radius
) {
	const float sm_near = 0.1;
	const float sm_far = 10000.0;
	float shadowMapSize = 1024.0; // 阴影贴图尺寸，可改为uniform动态传递
	
	// 1. 阴影相机视角位置
	vec3 viewPos = (shadowWorldView * vec4(pos, 1.0)).xyz;
	float distanceToLight = abs(viewPos.z);
	
	// 2. 投影变换与NDC转换
	vec4 projPos = shadowProj * shadowWorldView * vec4(pos, 1.0);
	vec3 nc = projPos.xyz / projPos.w;
	
	// 3. 阴影贴图UV坐标
	float u = nc.x * 0.5 + 0.5;
	float v = nc.y * 0.5 + 0.5;

	// 4. PCF软阴影采样偏移
	vec2 sampleStep = vec2(1.0 / (2.0 * shadowMapSize), 1.0 / (2.0 * shadowMapSize)) * 1.5;
	vec2 sampleLocations[9];
	sampleLocations[0] = vec2(0.0, 0.0);
	sampleLocations[1] = sampleStep;
	sampleLocations[2] = -sampleStep;
	sampleLocations[3] = vec2(sampleStep.x, -sampleStep.y);
	sampleLocations[4] = vec2(-sampleStep.x, sampleStep.y);
	sampleLocations[5] = vec2(0.0, sampleStep.y);
	sampleLocations[6] = vec2(0.0, -sampleStep.y);
	sampleLocations[7] = vec2(sampleStep.x, 0.0);
	sampleLocations[8] = vec2(-sampleStep.x, 0.0);

	// 5. 逐个采样（j循环：普通数组，合法）
	float visibleSamples = 0.0;
	float numSamples = 0.0;
	float bias = radius * 2.0;

	for(int j = 0; j < 9; j++){
		vec4 depthMapValue = texture(shadowMap, vec2(u, v) + sampleLocations[j]);
		float linearDepthFromSM = depthMapValue.x + bias;
		float linearDepthFromViewer = distanceToLight;
		if(linearDepthFromSM > linearDepthFromViewer){
			visibleSamples += 1.0;
		}
		numSamples += 1.0;
	}

	float visibility = visibleSamples / numSamples;

	// 6. 判断是否在有效范围内
	bool isInValidRange = (u >= 0.0 && u <= 1.0 && v >= 0.0 && v <= 1.0) && 
						  (nc.x >= -1.0 && nc.x <= 1.0 && nc.y >= -1.0 && nc.y <= 1.0 && nc.z >= -1.0 && nc.z <= 1.0);

	return isInValidRange ? visibility : 1.0;
}
#endif

// formula adapted from: http://www.dfstudios.co.uk/articles/programming/image-programming-algorithms/image-processing-algorithms-part-5-contrast-adjustment/
float getContrastFactor(float contrast){
	return (1.0158730158730156 * (contrast + 1.0)) / (1.0158730158730156 - contrast);
}

vec3 getRGB(){
	vec3 rgb = color;
	
	rgb = pow(rgb, vec3(uRGB_gbc.x));
	rgb = rgb + uRGB_gbc.y;
	rgb = (rgb - 0.5) * getContrastFactor(uRGB_gbc.z) + 0.5;
	rgb = clamp(rgb, 0.0, 1.0);

	return rgb;
}

float getIntensity(){
	float w = (intensity - intensityRange.x) / (intensityRange.y - intensityRange.x);
	w = pow(w, uIntensity_gbc.x);
	w = w + uIntensity_gbc.y;
	w = (w - 0.5) * getContrastFactor(uIntensity_gbc.z) + 0.5;
	w = clamp(w, 0.0, 1.0);

	return w;
}

vec3 getGpsTime(){

	float w = (gpsTime + uGpsOffset) * uGpsScale;


	vec3 c = texture(gradient, vec2(w, 1.0 - w)).rgb;


	// vec2 r = uNormalizedGpsBufferRange;
	// float w = gpsTime * (r.y - r.x) + r.x;
	// w = clamp(w, 0.0, 1.0);
	// vec3 c = texture(gradient, vec2(w,1.0-w)).rgb;
	
	return c;
}

vec3 getElevation(){
	vec4 world = modelMatrix * vec4( position, 1.0 );
	float w = (world.z - elevationRange.x) / (elevationRange.y - elevationRange.x);
	vec3 cElevation = texture(gradient, vec2(w,1.0-w)).rgb;
	
	return cElevation;
}

vec4 getClassification(){
	vec2 uv = vec2(classification / 255.0, 0.5);
	vec4 classColor = texture(classificationLUT, uv);
	
	return classColor;
}

vec3 getReturns(){

	// 0b 00_000_111
	float rn = mod(returnNumber, 8.0);
	// 0b 00_111_000
	float nr = mod(returnNumber / 8.0, 8.0);

	if(nr <= 1.0){
		return vec3(1.0, 0.0, 0.0);
	}else{
		return vec3(0.0, 1.0, 0.0);
	}

	// return vec3(nr / 4.0, 0.0, 0.0);

	// if(nr == 1.0){
	// 	return vec3(1.0, 1.0, 0.0);
	// }else{
	// 	if(rn == 1.0){
	// 		return vec3(1.0, 0.0, 0.0);
	// 	}else if(rn == nr){
	// 		return vec3(0.0, 0.0, 1.0);
	// 	}else{
	// 		return vec3(0.0, 1.0, 0.0);
	// 	}
	// }

	// if(numberOfReturns == 1.0){
	// 	return vec3(1.0, 1.0, 0.0);
	// }else{
	// 	if(returnNumber == 1.0){
	// 		return vec3(1.0, 0.0, 0.0);
	// 	}else if(returnNumber == numberOfReturns){
	// 		return vec3(0.0, 0.0, 1.0);
	// 	}else{
	// 		return vec3(0.0, 1.0, 0.0);
	// 	}
	// }
}

vec3 getReturnNumber(){
	if(numberOfReturns == 1.0){
		return vec3(1.0, 1.0, 0.0);
	}else{
		if(returnNumber == 1.0){
			return vec3(1.0, 0.0, 0.0);
		}else if(returnNumber == numberOfReturns){
			return vec3(0.0, 0.0, 1.0);
		}else{
			return vec3(0.0, 1.0, 0.0);
		}
	}
}

vec3 getNumberOfReturns(){
	float value = numberOfReturns;

	float w = value / 6.0;

	vec3 color = texture(gradient, vec2(w, 1.0 - w)).rgb;

	return color;
}

vec3 getSourceID(){
	float w = mod(pointSourceID, 10.0) / 10.0;
	return texture(gradient, vec2(w,1.0 - w)).rgb;
}

vec3 getCompositeColor(){
	vec3 c;
	float w;

	c += wRGB * getRGB();
	w += wRGB;
	
	c += wIntensity * getIntensity() * vec3(1.0, 1.0, 1.0);
	w += wIntensity;
	
	c += wElevation * getElevation();
	w += wElevation;
	
	c += wReturnNumber * getReturnNumber();
	w += wReturnNumber;
	
	c += wSourceID * getSourceID();
	w += wSourceID;
	
	vec4 cl = wClassification * getClassification();
	c += cl.a * cl.rgb;
	w += wClassification * cl.a;

	c = c / w;
	
	if(w == 0.0){
		//c = color;
		gl_Position = vec4(100.0, 100.0, 100.0, 0.0);
	}
	
	return c;
}


vec3 getNormal(){
	//vec3 n_hsv = vec3( modelMatrix * vec4( normal, 0.0 )) * 0.5 + 0.5; // (n_world.xyz + vec3(1.,1.,1.)) / 2.;
	vec3 n_view = normalize( vec3(modelViewMatrix * vec4( normal, 0.0 )) );
	return n_view;
}
bool applyBackfaceCulling() {
	// Black not facing vertices / Backface culling
	vec3 e = normalize(vec3(modelViewMatrix * vec4( position, 1. )));
	vec3 n = getNormal(); // normalize( vec3(modelViewMatrix * vec4( normal, 0.0 )) );

	if((uUseOrthographicCamera && n.z <= 0.) || (!uUseOrthographicCamera && dot( n, e ) >= 0.)) { 
		return true;
	} else {
		return false;
	}
}

#if defined(color_type_matcap)
// Matcap Material
vec3 getMatcap(){ 
	vec3 eye = normalize( vec3( modelViewMatrix * vec4( position, 1. ) ) ); 
	if(uUseOrthographicCamera) { 
		eye = vec3(0., 0., -1.);
	}
	vec3 r_en = reflect( eye, getNormal() ); // or r_en = e - 2. * dot( n, e ) * n;
	float m = 2. * sqrt(pow( r_en.x, 2. ) + pow( r_en.y, 2. ) + pow( r_en.z + 1., 2. ));
	vec2 vN = r_en.xy / m + .5;
	return texture(matcapTextureUniform, vN).rgb; 
}
#endif

vec3 getExtra(){

	float w = (aExtra + uExtraOffset) * uExtraScale;
	w = clamp(w, 0.0, 1.0);

	vec3 color = texture(gradient, vec2(w,1.0-w)).rgb;

	// vec2 r = uExtraNormalizedRange;

	// float w = aExtra * (r.y - r.x) + r.x;

	// w = (w - uExtraRange.x) / (uExtraRange.y - uExtraRange.x);

	// w = clamp(w, 0.0, 1.0);

	// vec3 color = texture(gradient, vec2(w,1.0-w)).rgb;

	return color;
}

vec4 getColor(){
	vec3 color;
	
	#ifdef color_type_rgba
		color = getRGB();
	#elif defined color_type_height || defined color_type_elevation
		color = getElevation();
	#elif defined color_type_rgb_height
		vec3 cHeight = getElevation();
		color = (1.0 - uTransition) * getRGB() + uTransition * cHeight;
	#elif defined color_type_depth
		float linearDepth = gl_Position.w;
		float expDepth = (gl_Position.z / gl_Position.w) * 0.5 + 0.5;
		color = vec3(linearDepth, expDepth, 0.0);
		//color = vec3(1.0, 0.5, 0.3);
	#elif defined color_type_intensity
		float w = getIntensity();
		color = vec3(w, w, w);
	#elif defined color_type_gps_time
		color = getGpsTime();
	#elif defined color_type_intensity_gradient
		float w = getIntensity();
		color = texture(gradient, vec2(w,1.0-w)).rgb;
	#elif defined color_type_color
		color = uColor;
	#elif defined color_type_level_of_detail
		float depth = getLOD();
		float w = depth / 10.0;
		color = texture(gradient, vec2(w,1.0-w)).rgb;
	#elif defined color_type_indices
		color = indices.rgb;
	#elif defined color_type_classification
		vec4 cl = getClassification(); 
		color = cl.rgb;
	#elif defined color_type_return_number
		color = getReturnNumber();
	#elif defined color_type_returns
		color = getReturns();
	#elif defined color_type_number_of_returns
		color = getNumberOfReturns();
	#elif defined color_type_source_id
		color = getSourceID();
	#elif defined color_type_point_source_id
		color = getSourceID();
	#elif defined color_type_normal
		color = (modelMatrix * vec4(normal, 0.0)).xyz;
	#elif defined color_type_phong
		color = color;
	#elif defined color_type_composite
		color = getCompositeColor();
	#elif defined color_type_matcap
		color = getMatcap();
	#else 
		color = getExtra();
	#endif
	
	if (backfaceCulling && applyBackfaceCulling()) {
		color = vec3(0.);
	}
	vec4 colorRet= vec4( color, 1.0 );

	return colorRet;
}

float getPointSize(){
	float pointSize = 1.0;
	
	float slope = tan(fov / 2.0);
	float projFactor = -0.5 * uScreenHeight / (slope * vViewPosition.z);

	float scale = length(
		modelViewMatrix * vec4(0, 0, 0, 1) - 
		modelViewMatrix * vec4(uOctreeSpacing, 0, 0, 1)
	) / uOctreeSpacing;
	projFactor = projFactor * scale;
	
	float r = uOctreeSpacing * 1.7;
	vRadius = r;
	#if defined fixed_point_size
		pointSize = size;
	#elif defined attenuated_point_size
		if(uUseOrthographicCamera){
			pointSize = size;
		}else{
			pointSize = size * spacing * projFactor;
			//pointSize = pointSize * projFactor;
		}
	#elif defined adaptive_point_size
		if(uUseOrthographicCamera) {
			float worldSpaceSize = 1.0 * size * r / getPointSizeAttenuation();
			pointSize = (worldSpaceSize / uOrthoWidth) * uScreenWidth;
		} else {
			float worldSpaceSize = 1.0 * size * r / getPointSizeAttenuation();
			pointSize = worldSpaceSize * projFactor;
		}
	#endif

	pointSize = max(minSize, pointSize);
	pointSize = min(maxSize, pointSize);
	
	vRadius = pointSize / projFactor;

	return pointSize;
}

#if defined(num_clippolygons) && num_clippolygons > 0
bool pointInClipPolygon(vec3 point, int polyIdx) {

	mat4 wvp = uClipPolygonWVP[polyIdx];
	//vec4 screenClipPos = uClipPolygonVP[polyIdx] * modelMatrix * vec4(point, 1.0);
	//screenClipPos.xy = screenClipPos.xy / screenClipPos.w * 0.5 + 0.5;

	vec4 pointNDC = wvp * vec4(point, 1.0);
	pointNDC.xy = pointNDC.xy / pointNDC.w;

	int j = uClipPolygonVCount[polyIdx] - 1;
	bool c = false;
	for(int i = 0; i < 8; i++) {
		if(i == uClipPolygonVCount[polyIdx]) {
			break;
		}

		//vec4 verti = wvp * vec4(uClipPolygonVertices[polyIdx * 8 + i], 1);
		//vec4 vertj = wvp * vec4(uClipPolygonVertices[polyIdx * 8 + j], 1);

		//verti.xy = verti.xy / verti.w;
		//vertj.xy = vertj.xy / vertj.w;

		//verti.xy = verti.xy / verti.w * 0.5 + 0.5;
		//vertj.xy = vertj.xy / vertj.w * 0.5 + 0.5;

		vec3 verti = uClipPolygonVertices[polyIdx * 8 + i];
		vec3 vertj = uClipPolygonVertices[polyIdx * 8 + j];

		if( ((verti.y > pointNDC.y) != (vertj.y > pointNDC.y)) && 
			(pointNDC.x < (vertj.x-verti.x) * (pointNDC.y-verti.y) / (vertj.y-verti.y) + verti.x) ) {
			c = !c;
		}
		j = i;
	}

	return c;
}
#endif

// 多边形棱柱内判定：世界点先经逆矩阵变回 volume 局部，再做局部 XY even-odd 射线
// 交叉（与 JS 版 pointInPolygonPrism 同一算法）+ 局部 Z 闭区间。顶点数上限 32
// （defines.js 的 MAX_CLIP_PRISM_VERTICES，改动需两处同步；循环按实际数量提前 break）。
#if defined(num_clipprisms) && num_clipprisms > 0
bool pointInClipPrism(vec3 worldPos, int prismIdx) {
	vec3 point = (uClipPrismInvMatrix[prismIdx] * vec4(worldPos, 1.0)).xyz;

	if(point.z < uClipPrismZRange[prismIdx].x || point.z > uClipPrismZRange[prismIdx].y) {
		return false;
	}

	int count = uClipPrismVCount[prismIdx];
	int j = count - 1;
	bool c = false;
	for(int i = 0; i < max_clip_prism_vertices; i++) {
		if(i == count) {
			break;
		}

		vec3 verti = uClipPrismVertices[prismIdx * max_clip_prism_vertices + i];
		vec3 vertj = uClipPrismVertices[prismIdx * max_clip_prism_vertices + j];

		if( ((verti.y > point.y) != (vertj.y > point.y)) &&
			(point.x < (vertj.x-verti.x) * (point.y-verti.y) / (vertj.y-verti.y) + verti.x) ) {
			c = !c;
		}
		j = i;
	}

	return c;
}
#endif

void doClipping(){

	{
		vec4 cl = getClassification(); 
		if(cl.a == 0.0){
			gl_Position = vec4(100.0, 100.0, 100.0, 0.0);
			
			return;
		}
	}

	#if defined(clip_return_number_enabled)
	{ // return number filter
		vec2 range = uFilterReturnNumberRange;
		if(returnNumber < range.x || returnNumber > range.y){
			gl_Position = vec4(100.0, 100.0, 100.0, 0.0);
			
			return;
		}
	}
	#endif

	#if defined(clip_number_of_returns_enabled)
	{ // number of return filter
		vec2 range = uFilterNumberOfReturnsRange;
		if(numberOfReturns < range.x || numberOfReturns > range.y){
			gl_Position = vec4(100.0, 100.0, 100.0, 0.0);
			
			return;
		}
	}
	#endif

	#if defined(clip_gps_enabled)
	{ // GPS time filter
		float time = (gpsTime + uGpsOffset) * uGpsScale;
		vec2 range = uFilterGPSTimeClipRange;

		if(time < range.x || time > range.y){
			gl_Position = vec4(100.0, 100.0, 100.0, 0.0);
			
			return;
		}
	}
	#endif

	#if defined(clip_point_source_id_enabled)
	{ // point source id filter
		vec2 range = uFilterPointSourceIDClipRange;
		if(pointSourceID < range.x || pointSourceID > range.y){
			gl_Position = vec4(100.0, 100.0, 100.0, 0.0);
			
			return;
		}
	}
	#endif

	int clipVolumesCount = 0;
	int insideCount = 0;
	int hideCount = 0;

	#if defined(num_clipboxes) && num_clipboxes > 0
		for(int i = 0; i < num_clipboxes; i++){
			vec4 clipPosition = clipBoxes[i] * modelMatrix * vec4( position, 1.0 );
			bool inside = -0.5 <= clipPosition.x && clipPosition.x <= 0.5;
			inside = inside && -0.5 <= clipPosition.y && clipPosition.y <= 0.5;
			inside = inside && -0.5 <= clipPosition.z && clipPosition.z <= 0.5;

			//bool inside = -clipBoxMeshes[i].x*0.5 <= clipPosition.x && clipPosition.x <= clipBoxMeshes[i].x*0.5;
			//inside = inside && -clipBoxMeshes[i].y*0.5 <= clipPosition.y && clipPosition.y <= clipBoxMeshes[i].y*0.5;
			//inside = inside && -clipBoxMeshes[i].z*0.5 <= clipPosition.z && clipPosition.z <= clipBoxMeshes[i].z*0.5;
			if(inside)
			{
				if(clipHides[i]==1)
				{
					hideCount++;
					vColor.a = 0.0;

				}
			}
			insideCount = insideCount + (inside ? 1 : 0);
			clipVolumesCount++;
		}	
	#endif

	#if defined(num_clippolygons) && num_clippolygons > 0
		for(int i = 0; i < num_clippolygons; i++) {
			bool inside = pointInClipPolygon(position, i);

			insideCount = insideCount + (inside ? 1 : 0);
			clipVolumesCount++;
		}
	#endif

	// 多边形棱柱（YDPolygonVolume）：与 clipboxes 并行参与 insideCount，
	// 下面的 clipTask/clipMethod 三态语义自动继承
	#if defined(num_clipprisms) && num_clipprisms > 0
		vec3 prismWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
		for(int i = 0; i < num_clipprisms; i++) {
			bool inside = pointInClipPrism(prismWorldPos, i);

			insideCount = insideCount + (inside ? 1 : 0);
			clipVolumesCount++;
		}
	#endif

	bool insideAny = insideCount > 0;
	bool insideAll = (clipVolumesCount > 0) && (clipVolumesCount == insideCount);

	if(clipMethod == CLIPMETHOD_INSIDE_ANY){
		if(insideAny && clipTask == CLIPTASK_HIGHLIGHT){
			vColor.r += 0.5;
			if(hideCount>0)
			{
				vColor.a = 0.0;
			}
		}else if(!insideAny && clipTask == CLIPTASK_SHOW_INSIDE){
			gl_Position = vec4(100.0, 100.0, 100.0, 1.0);
		}else if(insideAny && clipTask == CLIPTASK_SHOW_OUTSIDE){
			gl_Position = vec4(100.0, 100.0, 100.0, 1.0);
		}
	}else if(clipMethod == CLIPMETHOD_INSIDE_ALL){
		if(insideAll && clipTask == CLIPTASK_HIGHLIGHT){
			vColor.r += 0.5;
		}else if(!insideAll && clipTask == CLIPTASK_SHOW_INSIDE){
			gl_Position = vec4(100.0, 100.0, 100.0, 1.0);
		}else if(insideAll && clipTask == CLIPTASK_SHOW_OUTSIDE){
			gl_Position = vec4(100.0, 100.0, 100.0, 1.0);
		}
	}
}

// 设备区域着色：把落在着色盒内的点，按盒色 + 强度混合原色。与裁剪循环互不影响。
// num_colorboxes==0 时整个函数体被 define 剪掉 → 零开销。
void doColorize(){
	#if defined(num_colorboxes) && num_colorboxes > 0
		for(int i = 0; i < num_colorboxes; i++){
			vec4 p = colorBoxes[i] * modelMatrix * vec4(position, 1.0);
			bool inside = all(greaterThanEqual(p.xyz, vec3(-0.5)))
				&& all(lessThanEqual(p.xyz, vec3(0.5)));
			if(inside){
				vColor.rgb = mix(vColor.rgb, colorBoxColors[i], colorBoxIntensity[i]);
			}
		}
	#endif
}



// 
// ##     ##    ###    #### ##    ## 
// ###   ###   ## ##    ##  ###   ## 
// #### ####  ##   ##   ##  ####  ## 
// ## ### ## ##     ##  ##  ## ## ## 
// ##     ## #########  ##  ##  #### 
// ##     ## ##     ##  ##  ##   ### 
// ##     ## ##     ## #### ##    ## 
//

void main() {
	vec4 mvPosition = modelViewMatrix * vec4(position, 1.0 );
	vViewPosition = mvPosition.xyz;
	gl_Position = projectionMatrix * mvPosition;
	vLogDepth = log2(-mvPosition.z);

	//gl_Position = vec4(0.0, 0.0, 0.0, 1.0);
	//gl_PointSize = 5.0;

	// POINT SIZE
	float pointSize = getPointSize();
	//float pointSize = 2.0;
	gl_PointSize = pointSize;
	vPointSize = pointSize;

	// COLOR
	vColor = getColor();
	// vColor = vec3(1.0, 0.0, 0.0);

	//gl_Position = vec4(0.0, 0.0, 0.0, 1.0);
	//gl_Position = vec4(position.xzy / 1000.0, 1.0 );

	//gl_PointSize = 5.0;
	//vColor = vec3(1.0, 1.0, 1.0);

	// 只有在非拾取模式下才计算temperature颜色
	#if !defined color_type_indices
		if (showTemperature) {
			// 计算temperature颜色映射，按系数混合原色和温度色
			if (temperature < 1000.0) {
				vec3 tempColor = colorMap(temperature).rgb;
				vColor.rgb = vColor.rgb * (1.0 - 0.5) + tempColor * 0.5;
			}
		}
	#endif

	// only for "replacing" approaches
	// if(getLOD() != uLevel){
	// 	gl_Position = vec4(10.0, 10.0, 10.0, 1.0);
	// }


	#if defined hq_depth_pass
		float originalDepth = gl_Position.w;
		float adjustedDepth = originalDepth + 2.0 * vRadius;
		float adjust = adjustedDepth / originalDepth;

		mvPosition.xyz = mvPosition.xyz * adjust;
		gl_Position = projectionMatrix * mvPosition;
	#endif

	if(isSelected > 0.5) {
		vColor.r += 0.5;
	}

	// CLIPPING
	doClipping();

	// DEVICE REGION COLORIZE (设备区域着色，独立于裁剪)
	doColorize();

	#if defined(num_clipspheres) && num_clipspheres > 0
		for(int i = 0; i < num_clipspheres; i++){
			vec4 sphereLocal = uClipSpheres[i] * mvPosition;

			float distance = length(sphereLocal.xyz);

			if(distance < 1.0){
				float w = distance;
				vec3 cGradient = texture(gradient, vec2(w, 1.0 - w)).rgb;
				
				vColor = vec4(cGradient, 1.0);
				//vColor = cGradient;
				//vColor = cGradient * 0.7 + vColor * 0.3;
			}
		}
	#endif

// --------------------------
	// 替换原有i循环：硬编码处理多阴影
	// --------------------------
	#if defined(num_shadowmaps) && num_shadowmaps > 0
		// 阴影1（索引0：编译期常量）
		#if num_shadowmaps >= 1
			float visibility0 = calculateShadowVisibility(
				uShadowWorldView[0],
				uShadowProj[0],
				uShadowMap[0],
				position,
				vRadius
			);
			vColor.rgb = vColor.rgb * visibility0 + vColor.rgb * uShadowColor * (1.0 - visibility0);
		#endif

		// 阴影2（索引1：编译期常量）
		#if num_shadowmaps >= 2
			float visibility1 = calculateShadowVisibility(
				uShadowWorldView[1],
				uShadowProj[1],
				uShadowMap[1],
				position,
				vRadius
			);
			vColor.rgb = vColor.rgb * visibility1 + vColor.rgb * uShadowColor * (1.0 - visibility1);
		#endif

		// 阴影3（索引2：编译期常量，按需扩展）
		#if num_shadowmaps >= 3
			float visibility2 = calculateShadowVisibility(
				uShadowWorldView[2],
				uShadowProj[2],
				uShadowMap[2],
				position,
				vRadius
			);
			vColor.rgb = vColor.rgb * visibility2 + vColor.rgb * uShadowColor * (1.0 - visibility2);
		#endif

		// 阴影4（索引3：编译期常量，按需扩展）
		#if num_shadowmaps >= 4
			float visibility3 = calculateShadowVisibility(
				uShadowWorldView[3],
				uShadowProj[3],
				uShadowMap[3],
				position,
				vRadius
			);
			vColor.rgb = vColor.rgb * visibility3 + vColor.rgb * uShadowColor * (1.0 - visibility3);
		#endif
	#endif // num_shadowmaps > 0
}
`

Shaders["pointcloud.fs"] = `precision highp float;
precision highp int;

uniform mat4 viewMatrix;
uniform mat4 uViewInv;
uniform mat4 uProjInv;
uniform vec3 cameraPosition;


uniform mat4 projectionMatrix;
uniform float uOpacity;

uniform float blendHardness;
uniform float blendDepthSupplement;
uniform float fov;
uniform float uSpacing;
uniform float near;
uniform float far;
uniform float uPCIndex;
uniform float uScreenWidth;
uniform float uScreenHeight;

in vec4	vColor;
in vec4	vTemperatureColor;
in float	vLogDepth;
in vec3	vViewPosition;
in float	vRadius;

uniform float minTemperature;
uniform float maxTemperature;
uniform bool showTemperature;

layout(location = 0) out vec4 fragColor;
layout(location = 1) out float vPointSize;
layout(location = 2) out vec3 vPosition;

float specularStrength = 1.0;

void main() {

	// fragColor = vec4(vColor, 1.0);

	vec3 color = vColor.rgb;
	float depth = gl_FragCoord.z;

	#if defined(circle_point_shape) || defined(paraboloid_point_shape) 
		float u = 2.0 * gl_PointCoord.x - 1.0;
		float v = 2.0 * gl_PointCoord.y - 1.0;
	#endif
	
	#if defined(circle_point_shape) 
		float cc = u*u + v*v;
		if(cc > 1.0){
			discard;
		}
	#endif
		
	#if defined color_type_indices
		fragColor = vec4(color, uPCIndex / 255.0);
	#else
		fragColor = vec4(color, uOpacity);
	#endif

	#if defined paraboloid_point_shape
		float wi = 0.0 - ( u*u + v*v);
		vec4 pos = vec4(vViewPosition, 1.0);
		pos.z += wi * vRadius;
		float linearDepth = -pos.z;
		pos = projectionMatrix * pos;
		pos = pos / pos.w;
		float expDepth = pos.z;
		depth = (pos.z + 1.0) / 2.0;
		gl_FragDepth = depth;
		
		#if defined(color_type_depth)
			color.r = linearDepth;
			color.g = expDepth;
		#endif
		
		#if defined(use_edl)
			fragColor.a = log2(linearDepth);
		#endif
		
	#else
		#if defined(use_edl)
			fragColor.a = vLogDepth;
		#endif
	#endif

	#if defined(weighted_splats)
		float distance = 2.0 * length(gl_PointCoord.xy - 0.5);
		float weight = max(0.0, 1.0 - distance);
		weight = pow(weight, 1.5);

		fragColor.a = weight;
		fragColor.xyz = fragColor.xyz * weight;
	#endif
	if( vColor.a < 0.01)
	{
		discard;
	}
	//fragColor = vec4(0.0, 0.7, 0.0, 1.0);

	// 只有在非拾取模式下且showTemperature为true时才应用temperature颜色
	//#if !defined color_type_indices
	//	if ( showTemperature && vTemperatureColor.a > 0.0 )
	//	    fragColor = vTemperatureColor;
	//#endif
}


`

Shaders["pointcloud_sm.vs"] = `
precision mediump float;
precision mediump int;

in vec3 position;
in vec3 color;

uniform mat4 modelMatrix;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform mat4 viewMatrix;

uniform float uScreenWidth;
uniform float uScreenHeight;
uniform float near;
uniform float far;

uniform float uSpacing;
uniform float uOctreeSize;
uniform float uLevel;
uniform float uVNStart;

uniform sampler2D visibleNodes;

out float vLinearDepth;
out vec3 vColor;

#define PI 3.141592653589793



// ---------------------
// OCTREE
// ---------------------

#if defined(adaptive_point_size)
/**
 * number of 1-bits up to inclusive index position
 * number is treated as if it were an integer in the range 0-255
 *
 */
float numberOfOnes(float number, float index){
	float tmp = mod(number, pow(2.0, index + 1.0));
	float numOnes = 0.0;
	for(float i = 0.0; i < 8.0; i++){
		if(mod(tmp, 2.0) != 0.0){
			numOnes++;
		}
		tmp = floor(tmp / 2.0);
	}
	return numOnes;
}


/**
 * checks whether the bit at index is 1
 * number is treated as if it were an integer in the range 0-255
 *
 */
bool isBitSet(float number, float index){
	return mod(floor(number / pow(2.0, index)), 2.0) != 0.0;
}


/**
 * find the LOD at the point position
 */
float getLOD(){
	
	vec3 offset = vec3(0.0, 0.0, 0.0);
	float iOffset = uVNStart;
	float depth = uLevel;
	for(float i = 0.0; i <= 30.0; i++){
		float nodeSizeAtLevel = uOctreeSize  / pow(2.0, i + uLevel + 0.0);
		
		vec3 index3d = (position-offset) / nodeSizeAtLevel;
		index3d = floor(index3d + 0.5);
		float index = 4.0 * index3d.x + 2.0 * index3d.y + index3d.z;
		
		vec4 value = texture(visibleNodes, vec2(iOffset / 2048.0, 0.0));
		float mask = value.r * 255.0;
		if(isBitSet(mask, index)){
			// there are more visible child nodes at this position
			iOffset = iOffset + value.g * 255.0 * 256.0 + value.b * 255.0 + numberOfOnes(mask, index - 1.0);
			depth++;
		}else{
			// no more visible child nodes at this position
			return depth;
		}
		
		offset = offset + (vec3(1.0, 1.0, 1.0) * nodeSizeAtLevel * 0.5) * index3d;
	}
		
	return depth;
}

#endif

float getPointSize(){
	float pointSize = 1.0;
	
	float slope = tan(fov / 2.0);
	float projFactor =  -0.5 * uScreenHeight / (slope * vViewPosition.z);
	
	float r = uOctreeSpacing * 1.5;
	vRadius = r;
	#if defined fixed_point_size
		pointSize = size;
	#elif defined attenuated_point_size
		if(uUseOrthographicCamera){
			pointSize = size;			
		}else{
			pointSize = pointSize * projFactor;
		}
	#elif defined adaptive_point_size
		if(uUseOrthographicCamera) {
			float worldSpaceSize = 1.5 * size * r / getPointSizeAttenuation();
			pointSize = (worldSpaceSize / uOrthoWidth) * uScreenWidth;
		} else {
			float worldSpaceSize = 1.5 * size * r / getPointSizeAttenuation();
			pointSize = worldSpaceSize * projFactor;
		}
	#endif

	pointSize = max(minSize, pointSize);
	pointSize = min(maxSize, pointSize);
	
	vRadius = pointSize / projFactor;

	return pointSize;
}


void main() {

	vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
	vLinearDepth = gl_Position.w;

	float pointSize = getPointSize();
	gl_PointSize = pointSize;

}
`

Shaders["pointcloud_sm.fs"] = `
precision mediump float;
precision mediump int;

in vec3 vColor;
in float vLinearDepth;
out vec4 fragColor;

void main() {

	//fragColor = vec4(1.0, 0.0, 0.0, 1.0);
	//fragColor = vec4(vColor, 1.0);
	//fragColor = vec4(vLinearDepth, pow(vLinearDepth, 2.0), 0.0, 1.0);
	fragColor = vec4(vLinearDepth, vLinearDepth / 30.0, vLinearDepth / 30.0, 1.0);
	
}


`

Shaders["normalize.vs"] = `
precision mediump float;
precision mediump int;

in vec3 position;
in vec2 uv;

uniform mat4 projectionMatrix;
uniform mat4 modelViewMatrix;

out vec2 vUv;

void main() {
	vUv = uv;
	gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
}`

Shaders["normalize.fs"] = `precision mediump float;
precision mediump int;

uniform sampler2D uWeightMap;
uniform sampler2D uDepthMap;

in vec2 vUv;
out vec4 fragColor;

void main() {
	float depth = texture(uDepthMap, vUv).r;
	
	if(depth >= 1.0){
		discard;
	}

	fragColor = vec4(depth, 1.0, 0.0, 1.0);

	vec4 color = texture(uWeightMap, vUv); 
	color = color / color.w;
	
	fragColor = vec4(color.xyz, 1.0); 
	
	gl_FragDepth = depth;


}`

Shaders["normalize_and_edl.fs"] = `// 
// adapted from the EDL shader code from Christian Boucheny in cloud compare:
// https://github.com/cloudcompare/trunk/tree/master/plugins/qEDL/shaders/EDL
//

precision mediump float;
precision mediump int;

uniform sampler2D uWeightMap;
uniform sampler2D uEDLMap;
uniform sampler2D uDepthMap;

uniform float screenWidth;
uniform float screenHeight;
uniform vec2 neighbours[NEIGHBOUR_COUNT];
uniform float edlStrength;
uniform float radius;

in vec2 vUv;
out vec4 fragColor;

float response(float depth){
	vec2 uvRadius = radius / vec2(screenWidth, screenHeight);
	
	float sum = 0.0;
	
	for(int i = 0; i < NEIGHBOUR_COUNT; i++){
		vec2 uvNeighbor = vUv + uvRadius * neighbours[i];
		
		float neighbourDepth = texture(uEDLMap, uvNeighbor).a;

		if(neighbourDepth != 0.0){
			if(depth == 0.0){
				sum += 100.0;
			}else{
				sum += max(0.0, depth - neighbourDepth);
			}
		}
	}
	
	return sum / float(NEIGHBOUR_COUNT);
}

void main() {

	float edlDepth = texture(uEDLMap, vUv).a;
	float res = response(edlDepth);
	float shade = exp(-res * 300.0 * edlStrength);

	float depth = texture(uDepthMap, vUv).r;
	if(depth >= 1.0 && res == 0.0){
		discard;
	}
	
	vec4 color = texture(uWeightMap, vUv); 
	color = color / color.w;
	color = color * shade;

	fragColor = vec4(color.xyz, 1.0); 

	gl_FragDepth = depth;
}`

Shaders["edl.vs"] = `
precision mediump float;
precision mediump int;

in vec3 position;
in vec2 uv;

uniform mat4 projectionMatrix;
uniform mat4 modelViewMatrix;

out vec2 vUv;

void main() {
	vUv = uv;
	
	vec4 mvPosition = modelViewMatrix * vec4(position,1.0);

	gl_Position = projectionMatrix * mvPosition;
}`

Shaders["edl.fs"] = `// 
// adapted from the EDL shader code from Christian Boucheny in cloud compare:
// https://github.com/cloudcompare/trunk/tree/master/plugins/qEDL/shaders/EDL
//

precision mediump float;
precision mediump int;

uniform float screenWidth;
uniform float screenHeight;
uniform vec2 neighbours[NEIGHBOUR_COUNT];
uniform float edlStrength;
uniform float radius;
uniform float opacity;

uniform float uNear;
uniform float uFar;

uniform mat4 uProj;

uniform sampler2D uEDLColor;
uniform sampler2D uEDLDepth;

in vec2 vUv;
out vec4 fragColor;

float response(float depth){
	vec2 uvRadius = radius / vec2(screenWidth, screenHeight);
	
	float sum = 0.0;
	
	for(int i = 0; i < NEIGHBOUR_COUNT; i++){
		vec2 uvNeighbor = vUv + uvRadius * neighbours[i];
		
		float neighbourDepth = texture(uEDLColor, uvNeighbor).a;
		neighbourDepth = (neighbourDepth == 1.0) ? 0.0 : neighbourDepth;

		if(neighbourDepth != 0.0){
			if(depth == 0.0){
				sum += 100.0;
			}else{
				sum += max(0.0, depth - neighbourDepth);
			}
		}
	}
	
	return sum / float(NEIGHBOUR_COUNT);
}

void main(){
	vec4 cEDL = texture(uEDLColor, vUv);
	
	float depth = cEDL.a;
	depth = (depth == 1.0) ? 0.0 : depth;
	float res = response(depth);
	float shade = exp(-res * 300.0 * edlStrength);

	fragColor = vec4(cEDL.rgb * shade, opacity);

	{ // write regular hyperbolic depth values to depth buffer
		float dl = pow(2.0, depth);

		vec4 dp = uProj * vec4(0.0, 0.0, -dl, 1.0);
		float pz = dp.z / dp.w;
		float fragDepth = (pz + 1.0) / 2.0;

		gl_FragDepth = fragDepth;
	}

	if(depth == 0.0){
		discard;
	}

}
`

Shaders["blur.vs"] = `
out vec2 vUv;

void main() {
	vUv = uv;

	gl_Position =   projectionMatrix * modelViewMatrix * vec4(position,1.0);
}`

Shaders["blur.fs"] = `
uniform mat4 projectionMatrix;

uniform float screenWidth;
uniform float screenHeight;
uniform float near;
uniform float far;

uniform sampler2D map;

in vec2 vUv;
out vec4 fragColor;

void main() {

	float dx = 1.0 / screenWidth;
	float dy = 1.0 / screenHeight;

	vec3 color = vec3(0.0, 0.0, 0.0);
	color += texture(map, vUv + vec2(-dx, -dy)).rgb;
	color += texture(map, vUv + vec2(  0, -dy)).rgb;
	color += texture(map, vUv + vec2(+dx, -dy)).rgb;
	color += texture(map, vUv + vec2(-dx,   0)).rgb;
	color += texture(map, vUv + vec2(  0,   0)).rgb;
	color += texture(map, vUv + vec2(+dx,   0)).rgb;
	color += texture(map, vUv + vec2(-dx,  dy)).rgb;
	color += texture(map, vUv + vec2(  0,  dy)).rgb;
	color += texture(map, vUv + vec2(+dx,  dy)).rgb;

	color = color / 9.0;
	
	fragColor = vec4(color, 1.0);
}`

export {Shaders};